import { FC, useCallback, useEffect, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import EditModal from 'components/UI/EditModal';
import Button from 'components/UI/Button';
import { Content, ButtonGrid, CheckboxItem } from './styled';
import { FieldValues, SubmitHandler, useForm } from 'react-hook-form';
import InputField from 'components/UI/InputField';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import Heading, { Type as HeadingType } from 'components/UI/Heading';
import { Software } from 'models';
import { useParams } from 'react-router';
import { CreateRolloutAttribute, RolloutList } from 'models/rollout';
import {
  createRolloutSelector,
  storeRollout,
} from 'store/rolloutState/rolloutState';
import Loader from 'components/UI/Loader';
import SelectField from 'components/UI/SelectField';
import { showErrorToast } from 'utils/toast';
import Checkbox from 'components/UI/Checkbox';

type Props = {
  software: Software | null;
  open: boolean;
  onClose: () => void;
};

const CreateRolloutModal: FC<Props> = ({ software, open, onClose }) => {
  const dispatch = useAppDispatch();
  const intl = useIntl();
  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm();
  const { id } = useParams();

  const { isLoading, error, data }: RolloutList = useAppSelector(
    createRolloutSelector
  );

  useEffect(() => {
    if (error) {
      showErrorToast(
        `${intl.formatMessage({
          id: 'rollout_create.error',
        })}. ${error}`
      );
    }
    if (data) {
      console.log('rollout created close window');
      onClose();
    }
  }, [data, dispatch, error, id, intl, isLoading, onClose]);

  // Submit
  const onSubmit: SubmitHandler<FieldValues> = useCallback(
    (values: FieldValues) => {
      if (id) {
        dispatch(storeRollout(values, id));
      }
    },
    [dispatch, id]
  );

  const loadingSpinner = useMemo(() => {
    if (isLoading) {
      return <Loader center />;
    }
  }, [isLoading]);

  return (
    <EditModal title="rollout_create.title" open={open} close={onClose}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Content>
          <Heading type={HeadingType.h4}>
            <FormattedMessage
              id="rollout_create.sub_title"
              values={{
                software: software?.attributes.name,
              }}
            />
          </Heading>
          <InputField
            description="rollout_create.name"
            error={errors.name}
            register={register(CreateRolloutAttribute.Name, {
              required: {
                value: true,
                message: 'input.required',
              },
            })}
          />
          <SelectField
            name={CreateRolloutAttribute.Software}
            error={errors.software}
            label={intl.formatMessage({ id: 'rollout_create.software' })}
            register={register(CreateRolloutAttribute.Software, {
              required: {
                value: false,
                message: 'input.required',
              },
            })}
            defaultValue={`${software?.attributes.name} ${software?.attributes.version}`}
            options={[
              {
                value: `${software?.attributes.name} ${software?.attributes.version}`,
                label: `${software?.attributes.name} ${software?.attributes.version}`,
                key: software?.attributes.name ?? '1',
              },
            ]}
          />
          <InputField
            description="rollout_create.pnc"
            error={errors.pnc}
            register={register(CreateRolloutAttribute.Pnc, {
              required: { value: true, message: 'input.required' },
            })}
          />
          <CheckboxItem>
            <label htmlFor={'testBox'}>
              <FormattedMessage id="rollout_create.excludeTestDevices" />
            </label>
            <Checkbox
              id="excludeTestDeviceBox"
              defaultChecked={true}
              register={register(CreateRolloutAttribute.ExcludeTestDevices)}
            />
          </CheckboxItem>
        </Content>
        <ButtonGrid>
          {loadingSpinner}
          <Button
            onClick={onClose}
            backgroundColor="transparent"
            color="primary"
          >
            <FormattedMessage id="modal.btn_close" />
          </Button>
          <Button submit>
            <FormattedMessage id="rollout_create.btn_save" />
          </Button>
        </ButtonGrid>
      </form>
    </EditModal>
  );
};

export default CreateRolloutModal;
